/**
 * The pure half of «Para», «Copia» and «Copia oculta» (contract v10.44, ADR 0038 of the
 * back): each field holds chips, an address or a group of the address book, and the text
 * still being typed. Out of the component so the tests pin what is sent without rendering.
 */
import { type ContactSuggestion, peopleText } from '@/shared/contacts/types';
import { addressesFrom, writtenAddresses } from '@/shared/emails/sending';

/** One chip of a recipient field. A group is never sent as addresses: it goes in
 * `group_ids`, and the back sends it as a blind copy to each of its members. */
export type Recipient =
  | { kind: 'address'; address: string; name?: string | null | undefined }
  | { kind: 'group'; id: string; name: string; members: number };

/** A field as the form keeps it: its chips and what is typed and not yet a chip. */
export interface RecipientsValue {
  chips: Recipient[];
  text: string;
}

/** Groups one message may be written to (`MAX_GROUPS` of the back). */
export const MAX_GROUPS = 10;

/** People one message may reach in all, the members of its groups counted
 * (`MAX_TOTAL_RECIPIENTS` of the back). */
export const MAX_TOTAL_RECIPIENTS = 100;

/** What the form says past `MAX_TOTAL_RECIPIENTS`, in the back's own words. */
export const TOO_MANY_RECIPIENTS = `Como mucho ${MAX_TOTAL_RECIPIENTS} destinatarios por correo, contando los de los grupos.`;

/** What the hint under the fields says once a group is in any of them. */
export const GROUP_HINT =
  'Un grupo va en copia oculta a cada persona: nadie ve las direcciones de los demás.';

/** And when «Para» is left empty because the message only goes to groups. */
export const ONLY_GROUPS_HINT =
  'Sin nadie en «Para», el correo lleva la dirección del buzón desde el que sale.';

/** The fields of `defaults` as chips: addresses only. */
export function recipientsFrom(addresses: readonly string[] | null | undefined): RecipientsValue {
  return {
    chips: (addresses ?? []).map((address) => ({ kind: 'address', address })),
    text: '',
  };
}

/** Whether something typed already reads as a whole address (a space then makes a chip). */
export function looksLikeAddress(text: string): boolean {
  return /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(text.trim());
}

/**
 * Whether what is typed already holds a whole address somewhere: «ana@x.es», or «Ana Pérez
 * <ana@x.es>» typed or pasted in one go. Then Enter takes it as written and never swaps it
 * for a suggestion, which might be another address of somebody with the same name.
 */
export function holdsAddress(text: string): boolean {
  return /[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]+/.test(text);
}

/** Whether what is typed ends in «Nombre <dirección>», closed: a space then makes a chip. */
export function endsInBracketedAddress(text: string): boolean {
  return /<[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+>\s*$/.test(text);
}

function hasAddress(chips: readonly Recipient[], address: string): boolean {
  const key = address.toLowerCase();
  return chips.some((chip) => chip.kind === 'address' && chip.address.toLowerCase() === key);
}

/**
 * Turn what was typed or pasted into chips, after the ones the field has: several
 * addresses separated by commas, semicolons, spaces or lines, «Nombre <dirección>» as that
 * address with its name (a comma inside the name, as Outlook writes «Pérez, Ana», does
 * not split it), each once. Something that is not an address still becomes a chip,
 * marked, so the field says what is wrong with it instead of swallowing it.
 */
export function commitText(value: RecipientsValue, typed = value.text): RecipientsValue {
  const chips = [...value.chips];
  for (const { address, name } of writtenAddresses(typed)) {
    if (!hasAddress(chips, address)) chips.push({ kind: 'address', address, name });
  }
  return { chips, text: '' };
}

/** A suggestion picked from the list, as a chip; one already there is not added twice. */
export function addSuggestion(value: RecipientsValue, pick: ContactSuggestion): RecipientsValue {
  if (pick.kind === 'group') {
    const there = value.chips.some((chip) => chip.kind === 'group' && chip.id === pick.id);
    if (there) return { ...value, text: '' };
    return {
      chips: [
        ...value.chips,
        { kind: 'group', id: pick.id, name: pick.label, members: pick.members ?? 0 },
      ],
      text: '',
    };
  }
  const address = pick.address ?? '';
  if (!address || hasAddress(value.chips, address)) return { ...value, text: '' };
  return {
    chips: [...value.chips, { kind: 'address', address, name: pick.name ?? null }],
    text: '',
  };
}

/**
 * The addresses a field sends: its address chips and what is still typed, each once. The
 * chips are not glued in front of the text: a chip that is not an address, followed by
 * «Ana <ana@x.es>», would read as Outlook's «Apellido, Nombre <dirección>» and vanish.
 */
export function addressesOf(value: RecipientsValue): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const chip of value.chips) {
    if (chip.kind !== 'address' || seen.has(chip.address.toLowerCase())) continue;
    seen.add(chip.address.toLowerCase());
    out.push(chip.address);
  }
  for (const address of addressesFrom(value.text)) {
    if (seen.has(address.toLowerCase())) continue;
    seen.add(address.toLowerCase());
    out.push(address);
  }
  return out;
}

/**
 * How many people a message reaches, as the back counts them before refusing more than
 * `MAX_TOTAL_RECIPIENTS`: the addresses of the three fields, each once across them, every
 * member of each group (a group in two fields counts once), and the mailbox's own address
 * that the back puts in «Para» when only groups were written to. The form does not know
 * who is in a group, so somebody in a group and in a field, or in two groups, counts
 * twice here and once in the back: never fewer than the back counts.
 */
export function recipientsCount(to: RecipientsValue, ...copies: RecipientsValue[]): number {
  const values = [to, ...copies];
  const addresses = new Set(
    values.flatMap((value) => addressesOf(value).map((address) => address.toLowerCase())),
  );
  const groups = new Map<string, number>();
  for (const value of values) {
    for (const chip of value.chips) if (chip.kind === 'group') groups.set(chip.id, chip.members);
  }
  const members = [...groups.values()].reduce((sum, count) => sum + count, 0);
  const mailbox = groups.size > 0 && addressesOf(to).length === 0 ? 1 : 0;
  return addresses.size + members + mailbox;
}

/** The groups of all the fields, each once, in the order they were picked. */
export function groupsOf(...values: RecipientsValue[]): { id: string; name: string }[] {
  const seen = new Set<string>();
  const out: { id: string; name: string }[] = [];
  for (const value of values) {
    for (const chip of value.chips) {
      if (chip.kind !== 'group' || seen.has(chip.id)) continue;
      seen.add(chip.id);
      out.push({ id: chip.id, name: chip.name });
    }
  }
  return out;
}

/** «Tiendas · 12 personas», the line of a group in the suggestions and its chip's title. */
export function groupLine(name: string, members: number): string {
  return `${name} · ${peopleText(members)}`;
}

/** How an address chip reads: «Ana Pérez <ana@x.es>», or the bare address. */
export function addressLabel(chip: { address: string; name?: string | null | undefined }): string {
  return chip.name ? `${chip.name} <${chip.address}>` : chip.address;
}

/** Whether a pasted text holds more than one address, to be split into chips. */
export function isSeveral(pasted: string): boolean {
  return /[,;\n]/.test(pasted) || (pasted.match(/@/g)?.length ?? 0) > 1;
}
